import React from 'react'

/**
 * Keyboard-only skip-link. Renders as the first focusable child of a layout
 * so keyboard users can bypass the sidebar/nav and jump to page content.
 *
 * Visually hidden until the link receives keyboard focus. On focus, renders
 * as a styled anchor pinned to the top-left of the viewport. Clicking/Entering
 * moves focus to the element with id={targetId} (default "main-content").
 *
 * Layouts MUST place <SkipLink /> as the first child of the layout return.
 * The <main> (or equivalent) landmark receiving focus MUST carry
 * id="main-content" (or match the targetId prop).
 *
 * Per brand-guide §1a (WCAG AA Interaction State Rules) and OQ2 research
 * note (2026-04-20).
 */
export interface SkipLinkProps {
  /** DOM id of the element to jump to. Default: 'main-content'. */
  targetId?: string
  /** Visible link text. Default: 'Skip to main content'. */
  label?: string
}

export const SkipLink: React.FC<SkipLinkProps> = ({
  targetId = 'main-content',
  label = 'Skip to main content',
}) => (
  <a
    href={`#${targetId}`}
    className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[9999] focus:px-4 focus:py-2 focus:bg-card focus:text-primary focus:text-sm focus:font-medium focus:rounded-md focus:border focus:border-border focus:shadow-md focus:outline-none"
  >
    {label}
  </a>
)
